import { useEffect, useState } from 'react';
import { toast } from '../toast';
import { adminImpersonate } from '../api';
import { useAuth } from '../auth';

const KEY_STORAGE = 'wcfantasy.admin-key';
const API_BASE = (import.meta.env.VITE_API_BASE as string | undefined) ?? '';

const PLAYERS = ['Armands', 'Edgars', 'Valters', 'Emīls', 'Einārs', 'Raivis', 'Edijs', 'Jānis'];

interface Recipient { name: string; devices: number; last_subscribed_at: string | null }

export default function AdminPanel() {
  const { setSession } = useAuth();
  const [impersonateUser, setImpersonateUser] = useState('');
  const [impersonating, setImpersonating] = useState(false);
  const [key, setKey] = useState<string>(() => {
    try { return localStorage.getItem(KEY_STORAGE) ?? ''; } catch { return ''; }
  });
  const [keyInput, setKeyInput] = useState('');
  const [verified, setVerified] = useState(false);
  const [verifying, setVerifying] = useState(false);

  const [recipients, setRecipients] = useState<Recipient[]>([]);
  const [publicDevices, setPublicDevices] = useState(0);
  const [pickAll, setPickAll] = useState(true);
  const [selected, setSelected] = useState<Set<string>>(new Set(PLAYERS));
  const [includePublic, setIncludePublic] = useState(false);

  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [link, setLink] = useState('');
  const [push, setPush] = useState(true);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState<{ bellWrites: number; pushDelivered: number; publicDelivered: number; recipients: string[] } | null>(null);

  async function verify(k: string) {
    setVerifying(true);
    try {
      const res = await fetch(`${API_BASE}/api/admin/recipients`, { headers: { 'X-Admin-Key': k } });
      if (!res.ok) throw new Error(res.status === 401 ? 'Invalid admin key' : `${res.status}`);
      const data = await res.json() as { recipients: Recipient[]; publicDevices?: number };
      setRecipients(data.recipients);
      setPublicDevices(data.publicDevices ?? 0);
      setVerified(true);
      try { localStorage.setItem(KEY_STORAGE, k); } catch {}
      setKey(k);
    } catch (e) {
      toast.error((e as Error).message);
      setVerified(false);
    } finally {
      setVerifying(false);
    }
  }

  useEffect(() => { if (key) verify(key); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  function logout() {
    try { localStorage.removeItem(KEY_STORAGE); } catch {}
    setKey(''); setKeyInput(''); setVerified(false); setRecipients([]);
  }

  function toggle(p: string) {
    const next = new Set(selected);
    if (next.has(p)) next.delete(p); else next.add(p);
    setSelected(next);
    setPickAll(next.size === PLAYERS.length);
  }

  function toggleAll() {
    if (pickAll) { setSelected(new Set()); setPickAll(false); }
    else { setSelected(new Set(PLAYERS)); setPickAll(true); }
  }

  async function send() {
    if (!title.trim()) { toast.error('Title required.'); return; }
    if (selected.size === 0 && !includePublic) { toast.error('Pick at least one recipient or include public.'); return; }
    setSending(true);
    setResult(null);
    try {
      const res = await fetch(`${API_BASE}/api/admin/notify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Admin-Key': key },
        body: JSON.stringify({
          title: title.trim(),
          body: body.trim() || undefined,
          link: link.trim() || undefined,
          kind: 'announcement',
          recipients: pickAll ? 'all' : Array.from(selected),
          push,
          includePublic,
        }),
      });
      const data = await res.json() as any;
      if (!res.ok) throw new Error(data.error ?? 'Send failed');
      setResult({ bellWrites: data.bellWrites, pushDelivered: data.pushDelivered, publicDelivered: data.publicDelivered ?? 0, recipients: data.recipients });
      toast.success(`Sent to ${data.recipients.length} (bell ${data.bellWrites}, push ${data.pushDelivered}, public ${data.publicDelivered ?? 0})`);
      setTitle(''); setBody(''); setLink('');
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setSending(false);
    }
  }

  if (!verified) {
    return (
      <section className="card p-3 space-y-2" data-allow-select>
        <h2 className="text-xs uppercase tracking-widest text-dim">Admin</h2>
        <p className="text-[11px] text-dim">Enter your admin key to compose and send notifications to league members.</p>
        <div className="flex gap-2">
          <input
            type="password"
            value={keyInput} onChange={(e) => setKeyInput(e.target.value)}
            placeholder="Admin key"
            className="flex-1 bg-ink-800 border border-ink-700 rounded-lg px-3 py-2 text-sm text-chalk font-mono"
          />
          <button
            onClick={() => verify(keyInput)} disabled={!keyInput || verifying}
            className="h-9 px-3 rounded-lg bg-pitch-500 text-ink-950 text-xs font-semibold disabled:opacity-50"
          >{verifying ? '…' : 'Verify'}</button>
        </div>
      </section>
    );
  }

  async function doImpersonate() {
    if (!impersonateUser) return;
    setImpersonating(true);
    try {
      const r = await adminImpersonate(impersonateUser, key);
      setSession(r.token, r.name);
      toast.success(`Signed in as ${r.name}`);
      setImpersonateUser('');
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setImpersonating(false);
    }
  }

  return (
    <section className="card p-3 space-y-3" data-allow-select>
      <div className="flex items-center justify-between">
        <h2 className="text-xs uppercase tracking-widest text-dim">Admin · Compose notification</h2>
        <button onClick={logout} className="text-[10px] text-dim hover:text-red-400">Forget key</button>
      </div>

      <div className="rounded-lg bg-ink-800/60 p-2.5">
        <div className="text-[10px] uppercase tracking-wider text-dim mb-1.5">Impersonate user (testing)</div>
        <div className="flex gap-2">
          <select
            value={impersonateUser} onChange={(e) => setImpersonateUser(e.target.value)}
            className="flex-1 bg-ink-800 border border-ink-700 rounded-lg px-2 py-1.5 text-xs text-chalk"
          >
            <option value="">Pick a user…</option>
            {PLAYERS.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
          <button
            onClick={doImpersonate}
            disabled={!impersonateUser || impersonating}
            className="h-8 px-3 rounded-lg bg-cyan-500/20 text-cyan-400 text-[11px] font-semibold ring-1 ring-cyan-500/30 disabled:opacity-50"
          >{impersonating ? '…' : 'Sign in as'}</button>
        </div>
        <div className="text-[10px] text-dim mt-1">Replaces your current session with that user's JWT.</div>
      </div>

      <label className="block">
        <span className="text-[10px] uppercase tracking-wider text-dim block mb-1">Title</span>
        <input
          value={title} onChange={(e) => setTitle(e.target.value)} maxLength={120}
          placeholder="MD1 captain pick locks in 30 min"
          className="w-full bg-ink-800 border border-ink-700 rounded-lg px-3 py-2 text-sm text-chalk"
        />
      </label>

      <label className="block">
        <span className="text-[10px] uppercase tracking-wider text-dim block mb-1">Body (optional)</span>
        <textarea
          value={body} onChange={(e) => setBody(e.target.value)} maxLength={400}
          rows={2}
          className="w-full bg-ink-800 border border-ink-700 rounded-lg px-3 py-2 text-sm text-chalk resize-y"
        />
      </label>

      <label className="block">
        <span className="text-[10px] uppercase tracking-wider text-dim block mb-1">Link / URL (optional)</span>
        <input
          value={link} onChange={(e) => setLink(e.target.value)}
          placeholder="/leaderboard"
          className="w-full bg-ink-800 border border-ink-700 rounded-lg px-3 py-2 text-sm text-chalk font-mono"
        />
      </label>

      <div>
        <div className="flex items-center justify-between mb-1.5">
          <span className="text-[10px] uppercase tracking-wider text-dim">Recipients</span>
          <button onClick={toggleAll} className="text-[10px] text-pitch-400">{pickAll ? 'Deselect all' : 'Select all'}</button>
        </div>
        <div className="grid grid-cols-4 gap-1.5">
          {recipients.map((r) => {
            const on = selected.has(r.name);
            return (
              <button
                key={r.name}
                onClick={() => toggle(r.name)}
                className={`px-2 py-1 rounded-md text-xs flex items-center justify-between gap-1 ${
                  on ? 'bg-pitch-500/20 text-pitch-400 ring-1 ring-pitch-500/40' : 'bg-ink-800 text-chalk'
                }`}
                title={r.last_subscribed_at ?? 'no devices'}
              >
                <span className="truncate">{r.name}</span>
                <span className="text-[9px] text-dim">{r.devices}</span>
              </button>
            );
          })}
        </div>
      </div>

      <label className="flex items-center justify-between gap-3 py-1 cursor-pointer">
        <div className="flex-1 min-w-0">
          <div className="text-sm">Send to lock screen too</div>
          <div className="text-[10px] text-dim">Off = bell only, no push</div>
        </div>
        <span
          role="switch" aria-checked={push}
          onClick={() => setPush(!push)}
          className={`shrink-0 inline-flex items-center w-10 h-6 rounded-full p-0.5 transition-colors ${push ? 'bg-pitch-500' : 'bg-ink-700'}`}
        >
          <span className={`block w-5 h-5 rounded-full bg-chalk transition-transform ${push ? 'translate-x-4' : ''}`} />
        </span>
      </label>

      <label className="flex items-center justify-between gap-3 py-1 cursor-pointer">
        <div className="flex-1 min-w-0">
          <div className="text-sm">Also broadcast to public</div>
          <div className="text-[10px] text-dim">{publicDevices} anonymous device{publicDevices === 1 ? '' : 's'} subscribed</div>
        </div>
        <span
          role="switch" aria-checked={includePublic}
          onClick={() => setIncludePublic(!includePublic)}
          className={`shrink-0 inline-flex items-center w-10 h-6 rounded-full p-0.5 transition-colors ${includePublic ? 'bg-pitch-500' : 'bg-ink-700'}`}
        >
          <span className={`block w-5 h-5 rounded-full bg-chalk transition-transform ${includePublic ? 'translate-x-4' : ''}`} />
        </span>
      </label>

      <button
        onClick={send}
        disabled={!title.trim() || sending}
        className="w-full h-10 rounded-lg bg-pitch-500 text-ink-950 font-semibold text-sm disabled:opacity-50"
      >{sending ? 'Sending…' : `Send to ${pickAll ? 'all' : selected.size}`}</button>

      {result && (
        <div className="text-[11px] text-dim">
          Last send: <span className="text-chalk">{result.recipients.length}</span> recipients
          · bell <span className="text-chalk">{result.bellWrites}</span>
          · push <span className="text-chalk">{result.pushDelivered}</span>
          · public <span className="text-chalk">{result.publicDelivered}</span>
        </div>
      )}
    </section>
  );
}
